/**
 * Identity Auth Store — Legend-State observable for new Identity auth flow
 *
 * This store is NEW, coexists with auth.store.ts.
 * NO tokens stored here — tokens are server-side (Redis).
 * Only stores UI flow state for the authentication process.
 */
import { observable } from '@legendapp/state';

export type IdentityAuthenticationStep =
    | 'EnterPhoneNumber'
    | 'SelectChannel'
    | 'VerifyOtp'
    | 'Register'
    | 'Authenticated';

interface IdentityAuthStoreType {
    /** Whether user is authenticated via Identity Provider */
    isAuthenticated: boolean;
    /** Current step in the OTP authentication flow */
    authenticationStep: IdentityAuthenticationStep;
    /** Challenge session from OTP request (in-memory only) */
    challengeSession?: string;
    /** Whether OTP flow detected a new user */
    isNewUser?: boolean;
    /** Phone number being used in current OTP flow */
    phoneNumber?: string;
    /** Selected notification channel */
    notificationChannel?: 'SMS' | 'ZALO';
    /** Loading state for async operations */
    isLoading: boolean;
    /** Error message from last operation */
    error?: string;
    /** Timestamp when OTP was last requested */
    otpStartTime?: number;
}

export const identityAuthStore$ = observable<IdentityAuthStoreType>({
    isAuthenticated: false,
    authenticationStep: 'EnterPhoneNumber',
    isLoading: false,
});

/**
 * Reset identity auth store to initial state (e.g., on logout or flow restart)
 */
export function clearIdentityAuthData(): void {
    identityAuthStore$.isAuthenticated.set(false);
    identityAuthStore$.authenticationStep.set('EnterPhoneNumber');
    identityAuthStore$.challengeSession.set(undefined);
    identityAuthStore$.isNewUser.set(undefined);
    identityAuthStore$.phoneNumber.set(undefined);
    identityAuthStore$.notificationChannel.set(undefined);
    identityAuthStore$.isLoading.set(false);
    identityAuthStore$.error.set(undefined);
    identityAuthStore$.otpStartTime.set(undefined);
}
